import React, { useState, useEffect, useContext } from 'react';
import { AppContext } from '../context/AppContext';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { 
    Activity, DollarSign, Wallet, TrendingUp, TrendingDown, 
    Briefcase, Archive, AlertCircle, Calendar 
} from 'lucide-react';

const AccountingDashboard = () => {
    const { authFetch } = useContext(AppContext);
    const { t } = useTranslation();

    const [financials, setFinancials] = useState(null);
    const [loading, setLoading] = useState(true);
    const [start, setStart] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10));
    const [end, setEnd] = useState(new Date().toISOString().slice(0, 10));

    const fetchFinancials = () => {
        setLoading(true);
        authFetch(`/api/accounting?report=dashboard&start=${start}&end=${end}`)
            .then(r => r.json())
            .then(d => {
                setFinancials(d);
                setLoading(false);
            })
            .catch(() => setLoading(false));
    };

    useEffect(() => { fetchFinancials(); }, [start, end]);

    const MetricCard = ({ title, amount, icon: Icon, colorClass, bgClass, isCurrency = true }) => (
        <div className="relative overflow-hidden p-6 rounded-2xl bg-white shadow-sm border border-gray-100 transition-all hover:shadow-md group">
            <div className="flex justify-between items-start mb-4">
                <div>
                    <h3 className="text-sm font-semibold text-gray-500">{title}</h3>
                </div>
                <div className={`p-3 rounded-xl ${bgClass} ${colorClass} transition-transform group-hover:scale-110`}>
                    <Icon size={20} />
                </div>
            </div>
            <div className="flex items-baseline gap-2">
                <p className={`text-3xl font-black ${colorClass}`}>
                    {parseFloat(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                </p>
                {isCurrency && <span className="text-sm font-medium text-gray-400">CFA</span>}
            </div>
        </div>
    );

    if (loading && !financials) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6 pb-12 animate-in fade-in duration-500">
            {/* Header section */}
            <div className="flex flex-col md:flex-row md:justify-between md:items-end gap-4 bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                <div>
                    <h2 className="text-3xl font-black text-gray-800 tracking-tight">{t('Financial Executive Dashboard')}</h2>
                    <p className="text-gray-500 text-sm mt-1">{t('Real-time double-entry ledger overview and KPIs')}</p>
                </div>
                <div className="flex items-center gap-3 bg-gray-50 p-1.5 rounded-xl border border-gray-200">
                    <div className="flex items-center bg-white rounded-lg px-3 py-2 shadow-sm">
                        <Calendar size={16} className="text-gray-400 mr-2" />
                        <input type="date" className="border-none bg-transparent outline-none text-sm font-bold text-gray-700" value={start} onChange={e => setStart(e.target.value)} />
                    </div>
                    <span className="text-gray-400 font-bold">→</span>
                    <div className="flex items-center bg-white rounded-lg px-3 py-2 shadow-sm">
                        <Calendar size={16} className="text-gray-400 mr-2" />
                        <input type="date" className="border-none bg-transparent outline-none text-sm font-bold text-gray-700" value={end} onChange={e => setEnd(e.target.value)} />
                    </div>
                </div>
            </div>

            {/* P&L KPIs */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <MetricCard 
                    title={t('Total Revenue')} 
                    amount={financials?.total_revenue} 
                    icon={TrendingUp}
                    colorClass="text-emerald-600" 
                    bgClass="bg-emerald-50" 
                />
                <MetricCard 
                    title={t('Cost of Goods Sold')} 
                    amount={financials?.total_cogs} 
                    icon={Archive}
                    colorClass="text-rose-600" 
                    bgClass="bg-rose-50" 
                />
                <MetricCard 
                    title={t('Operating Expenses')} 
                    amount={financials?.total_expenses} 
                    icon={TrendingDown}
                    colorClass="text-orange-500" 
                    bgClass="bg-orange-50" 
                />
                <MetricCard 
                    title={t('Net Profit (Period)')} 
                    amount={financials?.net_profit} 
                    icon={Activity}
                    colorClass={financials?.net_profit >= 0 ? "text-indigo-600" : "text-red-600"} 
                    bgClass={financials?.net_profit >= 0 ? "bg-indigo-50" : "bg-red-50"} 
                />
            </div>

            {/* Balance Sheet KPIs */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Liquidity & Assets */}
                <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <MetricCard 
                        title={t('Cash & Bank Balances')} 
                        amount={financials?.cash_balance} 
                        icon={Wallet}
                        colorClass="text-blue-600" 
                        bgClass="bg-blue-50" 
                    />
                    <MetricCard 
                        title={t('Inventory Value')} 
                        amount={financials?.inventory_value} 
                        icon={Briefcase}
                        colorClass="text-teal-600" 
                        bgClass="bg-teal-50" 
                    />
                    <MetricCard 
                        title={t('Accounts Receivable (AR)')} 
                        amount={financials?.accounts_receivable} 
                        icon={DollarSign}
                        colorClass="text-sky-600" 
                        bgClass="bg-sky-50" 
                    />
                    <MetricCard 
                        title={t('Accounts Payable (AP)')} 
                        amount={financials?.accounts_payable} 
                        icon={AlertCircle}
                        colorClass="text-pink-600" 
                        bgClass="bg-pink-50" 
                    />
                </div>

                {/* Balance Sheet Summary */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden flex flex-col h-full">
                    <div className="bg-gradient-to-r from-gray-50 to-white px-6 py-5 border-b border-gray-100">
                        <h3 className="font-bold text-gray-800 flex items-center">
                            {t('Balance Sheet Summary')}
                            <span className="text-[10px] uppercase font-bold tracking-wider text-white bg-indigo-500 px-2 py-0.5 rounded-full ml-3">Live</span>
                        </h3>
                    </div>
                    <div className="p-6 space-y-5 flex-1">
                        <div className="flex justify-between items-center pb-3 border-b border-gray-50 group hover:bg-gray-50 -mx-2 px-2 rounded-lg transition-colors">
                            <span className="text-gray-500 font-medium">{t('Total Assets')}</span>
                            <span className="font-black text-xl text-blue-700">{parseFloat(financials?.total_assets || 0).toLocaleString()} <span className="text-xs text-gray-400">CFA</span></span>
                        </div>
                        <div className="flex justify-between items-center pb-3 border-b border-gray-50 group hover:bg-gray-50 -mx-2 px-2 rounded-lg transition-colors">
                            <span className="text-gray-500 font-medium">{t('Total Liabilities')}</span>
                            <span className="font-black text-xl text-rose-600">{parseFloat(financials?.total_liabilities || 0).toLocaleString()} <span className="text-xs text-gray-400">CFA</span></span>
                        </div>
                        <div className="flex justify-between items-center pb-3 border-b border-gray-50 group hover:bg-gray-50 -mx-2 px-2 rounded-lg transition-colors">
                            <span className="text-gray-500 font-medium">{t('Owner\'s Equity')}</span>
                            <span className="font-black text-xl text-indigo-700">{parseFloat(financials?.owners_equity || 0).toLocaleString()} <span className="text-xs text-gray-400">CFA</span></span>
                        </div>
                        
                        <div className="pt-2 mt-auto">
                            <div className="flex justify-between items-center bg-gray-50 p-4 rounded-xl border border-gray-100">
                                <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">{t('Accounting Equation')}</span>
                                <span className="text-sm font-bold text-gray-600 font-mono bg-white px-2 py-1 rounded shadow-sm">
                                    A = L + E
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4">
                <Link to="/accounts" className="flex flex-col items-center justify-center p-6 bg-white rounded-2xl shadow-sm border border-gray-100 hover:border-indigo-300 hover:shadow-md transition-all group">
                    <div className="p-3 bg-indigo-50 text-indigo-600 rounded-full mb-3 group-hover:scale-110 transition-transform">
                        <Briefcase size={24} />
                    </div>
                    <span className="font-semibold text-gray-700">{t('Chart of Accounts')}</span>
                </Link>
                <Link to="/journal" className="flex flex-col items-center justify-center p-6 bg-white rounded-2xl shadow-sm border border-gray-100 hover:border-blue-300 hover:shadow-md transition-all group">
                    <div className="p-3 bg-blue-50 text-blue-600 rounded-full mb-3 group-hover:scale-110 transition-transform">
                        <Activity size={24} />
                    </div>
                    <span className="font-semibold text-gray-700">{t('Journal Entries')}</span>
                </Link>
                <Link to="/owner-settlement" className="flex flex-col items-center justify-center p-6 bg-white rounded-2xl shadow-sm border border-gray-100 hover:border-emerald-300 hover:shadow-md transition-all group">
                    <div className="p-3 bg-emerald-50 text-emerald-600 rounded-full mb-3 group-hover:scale-110 transition-transform">
                        <DollarSign size={24} />
                    </div>
                    <span className="font-semibold text-gray-700">{t('Owner Capital')}</span>
                </Link>
                <Link to="/reports/financial" className="flex flex-col items-center justify-center p-6 bg-white rounded-2xl shadow-sm border border-gray-100 hover:border-purple-300 hover:shadow-md transition-all group">
                    <div className="p-3 bg-purple-50 text-purple-600 rounded-full mb-3 group-hover:scale-110 transition-transform">
                        <Archive size={24} />
                    </div>
                    <span className="font-semibold text-gray-700">{t('Financial Reports')}</span>
                </Link>
            </div>
        </div>
    );
};

export default AccountingDashboard;
